import { format } from 'date-fns'
import * as Yup from 'yup'

import { EMPTY_LIST } from '../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import { FORMIK_EMPTY_VALUE } from '../../../constants/validation'
import i18n from '../../../i18n'
import { newLineToBr } from '../../../utils/common'
import { getInhabitantProfilePathname } from '../../../utils/link'
import { parseServerValidation } from '../../../utils/validation'
import { getFlatCreationFormSuccessNotification } from '../../Flat'
import { getFormikFormSelectFieldWithCreationValidationSchema } from '../../Formik'
import {
  getUserRoleSelectDwellerOption,
  USER_FORM_FIELD_NAME_BUILDING,
  USER_FORM_FIELD_NAME_CONTRACT_END,
  USER_FORM_FIELD_NAME_EMAIL,
  USER_FORM_FIELD_NAME_EXTERNAL_ID,
  USER_FORM_FIELD_NAME_FIRST_NAME,
  USER_FORM_FIELD_NAME_FLAT,
  USER_FORM_FIELD_NAME_LAST_NAME,
  USER_FORM_FIELD_NAME_MESSAGE,
  USER_FORM_FIELD_NAME_PHONE,
  USER_FORM_FIELD_NAME_ROLE,
  USER_FORM_FIELD_NAME_SEND_INVITATION,
  USER_FORM_FIELD_NAME_SEND_SMS_INVITATION,
  USER_FORM_PHONE_FIELD_MAX_LENGTH,
  USER_FORM_SERVER_ERROR_INVITATION_MESSAGE_TOO_LONG_VALUE_PREFIX,
  USER_INVITATION_FORM_FIELD_NAME_SUBJECT,
} from '../../User'

export function getInhabitantFormValidationSchema({ messageMaxLength }) {
  return Yup.object({
    [USER_FORM_FIELD_NAME_BUILDING]: Yup.object().required(true),
    [USER_FORM_FIELD_NAME_CONTRACT_END]: Yup.date().nullable(),
    [USER_FORM_FIELD_NAME_EMAIL]: Yup.string().email(
      i18n.t('Error.EnterValidEmailKey')
    ),
    [USER_FORM_FIELD_NAME_FIRST_NAME]: Yup.string().required(true),
    [USER_FORM_FIELD_NAME_FLAT]:
      getFormikFormSelectFieldWithCreationValidationSchema({
        required: true,
      }),
    [USER_FORM_FIELD_NAME_MESSAGE]: Yup.string().when(
      USER_FORM_FIELD_NAME_SEND_INVITATION,
      {
        is: true,
        then: schema =>
          schema
            .required(
              'Error.InhabitantFormEmptyInvitationTextWithActiveSendInvitationKey'
            )
            .max(
              messageMaxLength,
              'Error.ExceedUserInvitationTextMaxLengthKey'
            ),
      }
    ),
    [USER_FORM_FIELD_NAME_PHONE]: Yup.string()
      .nullable()
      .max(
        USER_FORM_PHONE_FIELD_MAX_LENGTH,
        i18n.t('Error.MaxLengthKey', {
          length: USER_FORM_PHONE_FIELD_MAX_LENGTH,
        })
      ),
    [USER_FORM_FIELD_NAME_ROLE]: Yup.object().required(true),
  })
}

export function getInhabitantFormInitialValues({ message, subject, t }) {
  return {
    [USER_FORM_FIELD_NAME_BUILDING]: null,
    [USER_FORM_FIELD_NAME_CONTRACT_END]: null,
    [USER_FORM_FIELD_NAME_EMAIL]: FORMIK_EMPTY_VALUE,
    [USER_FORM_FIELD_NAME_EXTERNAL_ID]: FORMIK_EMPTY_VALUE,
    [USER_FORM_FIELD_NAME_FIRST_NAME]: FORMIK_EMPTY_VALUE,
    [USER_FORM_FIELD_NAME_FLAT]: null,
    [USER_FORM_FIELD_NAME_LAST_NAME]: FORMIK_EMPTY_VALUE,
    [USER_FORM_FIELD_NAME_MESSAGE]: message,
    [USER_FORM_FIELD_NAME_PHONE]: FORMIK_EMPTY_VALUE,
    [USER_FORM_FIELD_NAME_ROLE]: getUserRoleSelectDwellerOption(t),
    [USER_FORM_FIELD_NAME_SEND_INVITATION]: true,
    [USER_INVITATION_FORM_FIELD_NAME_SUBJECT]: subject,
  }
}

export function getInhabitantFormCreationPayload(values) {
  return {
    [USER_FORM_FIELD_NAME_BUILDING]:
      values[USER_FORM_FIELD_NAME_BUILDING].value,
    [USER_FORM_FIELD_NAME_EMAIL]: values[USER_FORM_FIELD_NAME_EMAIL] ?? null,
    [USER_FORM_FIELD_NAME_FLAT]:
      values[USER_FORM_FIELD_NAME_FLAT]?.number ??
      values[USER_FORM_FIELD_NAME_FLAT],
    [USER_FORM_FIELD_NAME_PHONE]: values[USER_FORM_FIELD_NAME_PHONE] ?? null,
    [USER_FORM_FIELD_NAME_ROLE]: values[USER_FORM_FIELD_NAME_ROLE].value,
    external_id: values[USER_FORM_FIELD_NAME_EXTERNAL_ID] ?? null,
    move_out_date: values[USER_FORM_FIELD_NAME_CONTRACT_END]
      ? format(
          values[USER_FORM_FIELD_NAME_CONTRACT_END],
          DATE_API_DATA_FORMAT_SHORT
        )
      : null,
    name: values[USER_FORM_FIELD_NAME_FIRST_NAME],
    second_name: values[USER_FORM_FIELD_NAME_LAST_NAME],
    send_invitation: values[USER_FORM_FIELD_NAME_SEND_INVITATION],
    ...(values[USER_FORM_FIELD_NAME_SEND_INVITATION]
      ? {
          [USER_FORM_FIELD_NAME_MESSAGE]: newLineToBr(
            values[USER_FORM_FIELD_NAME_MESSAGE]
          ),
          send_sms: values[USER_FORM_FIELD_NAME_SEND_SMS_INVITATION],
          subject: values[USER_INVITATION_FORM_FIELD_NAME_SUBJECT],
        }
      : null),
  }
}

export function parseInhabitantFormInvitationMessageServerError(error) {
  if (error?.endsWith('Key')) {
    return i18n.t(error)
  }

  if (
    error?.includes(
      USER_FORM_SERVER_ERROR_INVITATION_MESSAGE_TOO_LONG_VALUE_PREFIX
    )
  ) {
    return i18n.t('Error.ExceedUserInvitationTextMaxLengthKey')
  }

  return i18n.t('Error.EmptyInvitationTextKey')
}

export function parseInhabitantFormServerError(error) {
  const parsedError = parseServerValidation(error)

  if (parsedError[USER_FORM_FIELD_NAME_MESSAGE]) {
    parsedError[USER_FORM_FIELD_NAME_MESSAGE] =
      parseInhabitantFormInvitationMessageServerError(
        parsedError[USER_FORM_FIELD_NAME_MESSAGE]
      )
  }

  return parsedError
}

export function getInhabitantCreationFormSuccessNotifications({
  availableSections,
  data,
  isFlatCreated,
  isUserProfileNGAvailable,
}) {
  const notifications = [
    {
      linkProps: {
        // The webapp profile lives outside the SPA router.
        reloadDocument: !isUserProfileNGAvailable,
        title: i18n.t('Notification.InhabitantCreatedViewProfileLinkKey'),
        to: getInhabitantProfilePathname({
          id: data.owner,
          isUserProfileNGAvailable,
        }),
      },
      text: i18n.t('Notification.InhabitantCreatedTextKey', {
        name: data.fullname,
      }),
      title: i18n.t(`Notification.InhabitantCreatedTitle${data.group}Key`),
      withCloseButton: true,
    },
    ...(isFlatCreated
      ? [
          getFlatCreationFormSuccessNotification({
            address: data.address_name,
            availableSections,
            id: data.flat_obj.id,
            number: data.flat_obj.number,
          }),
        ]
      : EMPTY_LIST),
  ]

  return notifications
}

export function getInhabitantCreationFormErrorNotification() {
  return {
    text: i18n.t('Notification.InhabitantCreationErrorTextKey'),
    title: i18n.t('Notification.InhabitantCreationErrorTitleKey'),
  }
}
